import React, { useState, useEffect, useCallback, useRef, useReducer } from 'react';
import { CircleCheck, Save, Star } from 'lucide-react';
import axios from 'axios'
import { useNavigate } from 'react-router-dom'

import BottomBufferTextarea from 'components/BottomBufferTextarea'
import WritingFontPicker from 'components/WritingFontPicker'
import WritingModeControlsPanel from 'components/WritingModeControlsPanel'
import { useCurrentUser } from 'contexts/CurrentUserContext'
import { useWritingMode } from 'contexts/WritingModeContext'
import {
  resolveWritingFontKey,
  updateWritingFontPreference,
  writingFontFamily,
} from 'lib/writingFont'
import {
  PANEL_CLOSE_GRACE_MS,
  PANEL_HOVER_OPEN_DELAY_MS,
  handlePanelEscape,
  initialPanelState,
  isUpperLeftQuadrant,
  reducePanelState,
} from 'lib/writingModePanel'

const saveDebounceTimeout = 10000
/** Bootstrap `md` breakpoint — desktop hover panel at md+, mobile floater below. */
export const WRITING_MODE_MD_MIN_WIDTH = 768

function useMatchMedia(query, defaultValue = false) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return defaultValue
    return window.matchMedia(query).matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(query)
    const onChange = (e) => setMatches(e.matches)
    setMatches(mql.matches)
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', onChange)
      return () => mql.removeEventListener('change', onChange)
    }
    mql.addListener(onChange)
    return () => mql.removeListener(onChange)
  }, [query])

  return matches
}

function useIsDesktopMdUp() {
  return useMatchMedia(`(min-width: ${WRITING_MODE_MD_MIN_WIDTH}px)`, true)
}

function usePrefersReducedMotion() {
  return useMatchMedia('(prefers-reduced-motion: reduce)', false)
}

const WinnerSelector = ({optionId, name, selected, disabled, onSelect, onDeselect}) => (
  <button
    className={`btn w-100 ${selected && 'btn-primary'}`}
    disabled={disabled}
    onClick={selected ? onDeselect : onSelect}
  >
    {selected &&
      <Star fill='yellow' size={18} style={{
        position: 'relative',
        top: '-2px',
        paddingRight: '3px'
      }}/>
    }
    {name}
  </button>
)

const ComparisonEditor = ({
  comparison,
  onSave,
  saving,
  justSaved,
  promptMe,
  editorLocked,
  prompting,
  onPromptCompleted,
  listId: listIdProp,
}) => {
  const currentUser = useCurrentUser()
  const { writingMode, setWritingMode } = useWritingMode()
  const navigate = useNavigate()
  const isDesktop = useIsDesktopMdUp()
  const prefersReducedMotion = usePrefersReducedMotion()
  const isDesktopRef = useRef(isDesktop)
  const prefersReducedMotionRef = useRef(prefersReducedMotion)
  const [winnerId, setWinnerId] = useState(comparison.winner_id)
  const [reasoning, setReasoning] = useState(comparison.reasoning)
  const [submitting, setSubmitting] = useState(false)
  const [enterControlVisible, setEnterControlVisible] = useState(true)
  const [panel, dispatchPanel] = useReducer(reducePanelState, undefined, initialPanelState)
  const [panelFadingOut, setPanelFadingOut] = useState(false)
  const [writingFontKey, setWritingFontKey] = useState(() =>
    resolveWritingFontKey(currentUser?.effective_writing_font)
  )
  const [fontSaving, setFontSaving] = useState(false)
  const textareaRef = useRef(null)
  const flushSaveRef = useRef(null)
  const shellRef = useRef(null)
  const panelRef = useRef(null)
  const hoverOpenTimerRef = useRef(null)
  const closeGraceTimerRef = useRef(null)
  const panelStateRef = useRef(panel)
  const reasoningFontFamily = writingFontFamily(writingFontKey)

  useEffect(() => {
    isDesktopRef.current = isDesktop
  }, [isDesktop])

  useEffect(() => {
    prefersReducedMotionRef.current = prefersReducedMotion
  }, [prefersReducedMotion])

  useEffect(() => {
    setWritingFontKey(resolveWritingFontKey(currentUser?.effective_writing_font))
  }, [currentUser?.effective_writing_font])

  const listId = listIdProp ?? comparison?.list_id ?? comparison?.list?.id

  useEffect(() => {
    panelStateRef.current = panel
  }, [panel])

  // Sync local state when props change
  useEffect(() => {
    setWinnerId(comparison.winner_id)
    setReasoning(comparison.reasoning)
  }, [comparison.winner_id, comparison.reasoning])

  // Handle prompt completion - scroll to bottom when prompt is just completed
  useEffect(() => {
    if (onPromptCompleted && textareaRef.current) {
      // Use setTimeout to ensure the DOM has updated after the comparison reload
      setTimeout(() => {
        textareaRef.current.scrollToBottom();
      }, 100);
    }
  }, [onPromptCompleted]);

  // Debounced save function with flush support
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      let pendingReasoning = null;
      const schedule = (newReasoning) => {
        pendingReasoning = newReasoning;
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          onSave({...comparison, reasoning: pendingReasoning});
          pendingReasoning = null;
        }, saveDebounceTimeout);
      };
      schedule.flush = () => {
        if (pendingReasoning === null) return;
        clearTimeout(timeoutId);
        onSave({...comparison, reasoning: pendingReasoning});
        pendingReasoning = null;
      };
      flushSaveRef.current = schedule.flush;
      return schedule;
    })(),
    [comparison, onSave]
  );

  const clearHoverOpenTimer = () => {
    if (hoverOpenTimerRef.current != null) {
      clearTimeout(hoverOpenTimerRef.current)
      hoverOpenTimerRef.current = null
    }
  }

  const clearCloseGraceTimer = () => {
    if (closeGraceTimerRef.current != null) {
      clearTimeout(closeGraceTimerRef.current)
      closeGraceTimerRef.current = null
    }
  }

  const closePanelNow = useCallback(() => {
    clearHoverOpenTimer()
    clearCloseGraceTimer()
    setPanelFadingOut(false)
    dispatchPanel({ type: 'close' })
  }, [])

  const selectWinner = (id) => {
    setSubmitting(true);
    setWinnerId(id)
    onSave({...comparison, winner_id: id, reasoning});
    setSubmitting(false);
  };

  // This pattern set(true), act, set(false) is probably an async problem
  const deselectWinner = () => {
    setSubmitting(true);
    setWinnerId(null)
    onSave({...comparison, winner_id: null, reasoning}) // fix: use cb here
    setSubmitting(false);
  }

  const focusTextareaAtEnd = () => {
    const ta = textareaRef.current?.getTextarea?.() || textareaRef.current
    if (!ta || typeof ta.focus !== 'function') return
    ta.focus()
    const len = ta.value?.length ?? 0
    if (typeof ta.setSelectionRange === 'function') {
      ta.setSelectionRange(len, len)
    }
  }

  const enterWritingMode = () => {
    setWritingMode(true)
    dispatchPanel({ type: 'reset' })
    setPanelFadingOut(false)
    // Focus after mode layout paints
    requestAnimationFrame(() => {
      focusTextareaAtEnd()
    })
  }

  const exitWritingMode = () => {
    flushSaveRef.current?.()
    clearHoverOpenTimer()
    clearCloseGraceTimer()
    setPanelFadingOut(false)
    dispatchPanel({ type: 'reset' })
    setWritingMode(false)
    setEnterControlVisible(true)
  }

  const handleNextComparison = () => {
    if (listId == null) return
    axios.post(`/api/lists/${listId}/next_comparison`)
      .then((response) => {
        const next = response.data
        navigate(`/lists/${listId}/comparisons/${next.id}`)
      })
      .catch(() => {
        // Keep mode; silent fail is OK for this slice (Comparison page shows errors via parent)
      })
  }

  const handleWritingFontChange = async (nextKey) => {
    const key = resolveWritingFontKey(nextKey)
    setWritingFontKey(key)
    // Keep SPA provision in sync for this session
    if (typeof window !== 'undefined' && window.currentUser) {
      window.currentUser = { ...window.currentUser, effective_writing_font: key }
    }
    setFontSaving(true)
    try {
      await updateWritingFontPreference(key, { axiosClient: axios })
    } catch (_err) {
      // Preference may still be applied locally; Settings remains source of truth on reload
    } finally {
      setFontSaving(false)
    }
  }

  useEffect(() => {
    if (!writingMode) return undefined

    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      const result = handlePanelEscape(panelStateRef.current)
      if (result.exitWritingMode) {
        exitWritingMode()
      } else {
        closePanelNow()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [writingMode, closePanelNow])

  // Outside click: immediate close + unpin (writing surface excluded — typing has its own dismiss rules)
  useEffect(() => {
    if (!writingMode) return undefined
    if (!panel.open && !panel.pinned) return undefined

    const onPointerDown = (e) => {
      const root = panelRef.current
      if (root && root.contains(e.target)) return
      // Clicks/focus in the reasoning field are not "outside clicks" for panel chrome
      if (e.target?.closest?.('.writing-surface-wrap')) return
      // Mobile floater owns its own toggle — don't treat as outside dismiss
      if (e.target?.closest?.('.writing-mode-mobile-floater')) return
      closePanelNow()
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [writingMode, panel.open, panel.pinned, closePanelNow])

  // Cleanup timers on unmount / leave mode
  useEffect(() => () => {
    clearHoverOpenTimer()
    clearCloseGraceTimer()
  }, [])

  const handleReasoningValue = (v) => {
    // BottomBufferTextarea also fires on click/keyup; only value changes matter
    if (v === reasoning) return
    setReasoning(v)
    debouncedSave(v)
    if (!writingMode) {
      setEnterControlVisible(false)
    } else if (!isDesktopRef.current) {
      // Mobile: any value-changing input closes the sheet immediately (no pin)
      closePanelNow()
    } else {
      dispatchPanel({ type: 'valueChangingKeystroke' })
    }
  }

  const handleMouseMove = (e) => {
    if (!writingMode) {
      setEnterControlVisible(true)
      return
    }

    // Desktop hover-open path only
    if (!isDesktopRef.current) return

    const shell = shellRef.current
    if (!shell) return
    const rect = shell.getBoundingClientRect()
    const inQuadrant = isUpperLeftQuadrant(e.clientX, e.clientY, rect)
    const current = panelStateRef.current

    if (inQuadrant) {
      clearCloseGraceTimer()
      setPanelFadingOut(false)
      if (!current.open && !current.pinned && hoverOpenTimerRef.current == null) {
        hoverOpenTimerRef.current = setTimeout(() => {
          hoverOpenTimerRef.current = null
          dispatchPanel({ type: 'open' })
        }, PANEL_HOVER_OPEN_DELAY_MS)
      }
    } else {
      clearHoverOpenTimer()
    }
  }

  const toggleMobileSheet = () => {
    if (panelStateRef.current.open) {
      closePanelNow()
    } else {
      dispatchPanel({ type: 'open' })
    }
  }

  const handlePanelMouseEnter = () => {
    clearCloseGraceTimer()
    setPanelFadingOut(false)
  }

  const handlePanelMouseLeave = () => {
    const current = panelStateRef.current
    if (current.pinned) return
    if (!current.open) return
    clearCloseGraceTimer()
    closeGraceTimerRef.current = setTimeout(() => {
      closeGraceTimerRef.current = null
      if (prefersReducedMotionRef.current) {
        setPanelFadingOut(false)
        dispatchPanel({ type: 'close' })
        return
      }
      // Slow fade-out then close
      setPanelFadingOut(true)
      closeGraceTimerRef.current = setTimeout(() => {
        closeGraceTimerRef.current = null
        setPanelFadingOut(false)
        dispatchPanel({ type: 'close' })
      }, 600)
    }, PANEL_CLOSE_GRACE_MS)
  }

  const fontPicker = (
    <WritingFontPicker
      value={writingFontKey}
      onChange={handleWritingFontChange}
      disabled={fontSaving}
    />
  )

  const saveStatus = (
    <span style={{ color: writingMode ? '#888' : '#d5d5d5' }} aria-live="polite">
      { saving && <Save /> }
      { justSaved && <CircleCheck /> }
    </span>
  )

  const winnerRow = (
    <div className="row mt-4">
      <div className="col-md-6">
        <div className="border p-3">
          <WinnerSelector
            optionId={comparison.a_id}
            name={comparison.a.name}
            selected={comparison.a_id == winnerId}
            disabled={submitting}
            onSelect={() => selectWinner(comparison.a.id)}
            onDeselect={() => deselectWinner()}
          />
        </div>
      </div>

      <div className="col-md-6">
        <div className="border p-3">
          <WinnerSelector
            optionId={comparison.b_id}
            name={comparison.b.name}
            selected={comparison.b_id == winnerId}
            disabled={submitting}
            onSelect={() => selectWinner(comparison.b.id)}
            onDeselect={() => deselectWinner()}
          />
        </div>
      </div>
    </div>
  )

  const promptMeButton = (
    <div className='row mt-2'>
      <div className='col-md-12'>
        <button
          className='btn btn-outline-secondary border'
          onClick={ () => promptMe(reasoning) }
          disabled={editorLocked || prompting}
        >
          {prompting ? 'Looking for the right question ...' : "I'm stuck — Ask me something"}
        </button>
      </div>
    </div>
  )

  const enterControl = !writingMode && (
    <button
      type="button"
      className="btn btn-sm btn-outline-secondary writing-mode-enter"
      onClick={enterWritingMode}
      aria-label="Enter writing mode"
      aria-hidden={enterControlVisible ? undefined : true}
      tabIndex={enterControlVisible ? 0 : -1}
      style={{
        position: 'absolute',
        top: '0.5rem',
        right: '0.5rem',
        zIndex: 2,
        opacity: enterControlVisible ? 1 : 0,
        pointerEvents: enterControlVisible ? 'auto' : 'none',
      }}
    >
      Focus
    </button>
  )

  // Keyboard path while floating control is visually hidden from typing
  const enterControlKeyboardPath = !writingMode && !enterControlVisible && (
    <button
      type="button"
      className="writing-mode-enter-keyboard"
      onClick={enterWritingMode}
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
      }}
    >
      Enter writing mode
    </button>
  )

  const textareaBlock = (
    <div
      className="form-group mt-4 writing-surface-wrap"
      style={{ position: 'relative' }}
      onMouseMove={handleMouseMove}
    >
      {!writingMode && <label htmlFor="reasoning">Your thoughts</label>}
      {enterControl}
      {enterControlKeyboardPath}
      <BottomBufferTextarea
        ref={textareaRef}
        id='reasoning'
        className='form-control'
        rows={writingMode ? undefined : '22'}
        aria-label="Your thoughts"
        style={{
          padding: '2em',
          fontFamily: reasoningFontFamily,
          ...(writingMode ? {
            background: 'transparent',
            color: '#e8e8e8',
            border: 'none',
            boxShadow: 'none',
            resize: 'none',
            minHeight: '70vh',
            height: 'calc(100vh - 8rem)',
          } : {}),
        }}
        value={reasoning}
        onValue={handleReasoningValue}
      />
    </div>
  )

  if (writingMode) {
    const panelVisible = panel.open || panel.pinned
    const sharedPanelProps = {
      comparison,
      listId,
      listName: comparison?.list?.name,
      winnerId,
      submitting,
      onSelectWinner: selectWinner,
      onDeselectWinner: deselectWinner,
      promptMe,
      reasoning,
      editorLocked,
      prompting,
      onNextComparison: handleNextComparison,
      fontPicker,
    }

    return (
      <div
        ref={shellRef}
        className="writing-mode-shell"
        data-writing-mode="true"
        data-writing-layout={isDesktop ? 'desktop' : 'mobile'}
        onMouseMove={handleMouseMove}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1050,
          background: '#0d0d0d',
          color: '#e8e8e8',
          display: 'flex',
          flexDirection: 'column',
          padding: isDesktop ? '1rem 1.25rem' : '0.75rem 0.75rem 4.5rem',
        }}
      >
        <div
          className="writing-mode-topbar d-flex justify-content-between align-items-start"
          style={{ flexShrink: 0, marginBottom: '0.75rem', position: 'relative' }}
        >
          <div>
            <div className="writing-mode-options-line" style={{ fontSize: '0.95rem', opacity: 0.85 }}>
              {comparison.a.name} vs {comparison.b.name}
              {isDesktop && (
                <span
                  className="writing-mode-panel-affordance"
                  aria-hidden="true"
                  style={{ marginLeft: '0.4rem', opacity: 0.5 }}
                  title="Hover for controls"
                >
                  ···
                </span>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {/* Quiet reserved slot for a future visual writing timer — layout only */}
            <div
              data-testid="writing-mode-timer-slot"
              className="writing-mode-timer-slot"
              aria-hidden="true"
              style={{
                width: '0.85rem',
                height: '0.85rem',
                borderRadius: '999px',
                border: '1px solid transparent',
                opacity: 0.35,
                flexShrink: 0,
              }}
            />
            {saveStatus}
            {isDesktop && (
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={exitWritingMode}
              >
                Exit focus
              </button>
            )}
          </div>

          {isDesktop && panelVisible && (
            <div
              ref={panelRef}
              className="writing-mode-desktop-panel-anchor"
              style={{
                position: 'absolute',
                top: '1.75rem',
                left: 0,
                zIndex: 3,
              }}
            >
              <WritingModeControlsPanel
                {...sharedPanelProps}
                showPin
                pinned={panel.pinned}
                onTogglePin={() => dispatchPanel({ type: 'togglePin' })}
                onMouseEnter={handlePanelMouseEnter}
                onMouseLeave={handlePanelMouseLeave}
                fadingOut={panelFadingOut && !panel.pinned}
              />
            </div>
          )}
        </div>

        <div
          className="writing-mode-column flex-grow-1"
          style={{
            width: '100%',
            maxWidth: isDesktop ? '100ch' : '100%',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          {textareaBlock}
        </div>

        {!isDesktop && (
          <>
            {panel.open && (
              <>
                <button
                  type="button"
                  data-testid="writing-mode-sheet-scrim"
                  aria-label="Dismiss controls"
                  onClick={closePanelNow}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 1055,
                    background: 'rgba(0,0,0,0.45)',
                    border: 'none',
                    padding: 0,
                  }}
                />
                <div
                  ref={panelRef}
                  className="writing-mode-mobile-sheet"
                  style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 1060,
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    padding: '0.75rem',
                    paddingBottom: '5rem',
                    background: '#141414',
                    borderTop: '1px solid #333',
                    borderTopLeftRadius: '0.75rem',
                    borderTopRightRadius: '0.75rem',
                  }}
                >
                  <WritingModeControlsPanel
                    {...sharedPanelProps}
                    showPin={false}
                    pinned={false}
                    style={{ maxWidth: '100%', minWidth: 0, boxShadow: 'none', border: 'none', background: 'transparent' }}
                  />
                </div>
              </>
            )}

            <div
              data-testid="writing-mode-mobile-floater"
              className="writing-mode-mobile-floater d-flex align-items-center justify-content-between gap-2"
              style={{
                position: 'fixed',
                left: '0.75rem',
                right: '0.75rem',
                bottom: '0.75rem',
                zIndex: 1070,
                background: '#1f1f1f',
                border: '1px solid #444',
                borderRadius: '999px',
                padding: '0.4rem 0.6rem',
                boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
              }}
            >
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                aria-label={panel.open ? 'Close controls' : 'Open controls'}
                aria-expanded={panel.open}
                onClick={toggleMobileSheet}
              >
                {panel.open ? 'Close' : 'Controls'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-light"
                onClick={exitWritingMode}
              >
                Exit focus
              </button>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className="card">
      <div className="card-body">
        <div className='row'>
          <div className='col-md-10'>
            <h5>Imagine these are your only two options. Write freely to explore them:</h5>
          </div>
          <div className='col-md-2 d-flex justify-content-end align-items-start'>
            {saveStatus}
          </div>
        </div>

        {winnerRow}
        {promptMeButton}
        {textareaBlock}
      </div>
    </div>
  );
};

export default ComparisonEditor;
